/**
 * Version Manager Service
 * Handles app version detection, cache clearing, and force updates
 */

import { PWA_STANDALONE_USED_KEY } from '@/constants/pwaStorage'

export interface VersionInfo {
  version: string
  gitCommit: string
  gitBranch: string
  buildDate: string
  timestamp: number
  hasUserFacingChanges: boolean
  updateMessage: string | null
  features: string[]
}

const VERSION_STORAGE_KEY = 'app_version'
const LAST_CLEAR_KEY = 'last_cache_clear'

class VersionManager {
  private currentVersion: VersionInfo | null = null
  private previousVersion: string | null = null
  private isInitialized = false

  /**
   * Initialize version manager and check for updates
   * Returns true if version changed and caches were cleared
   */
  public async initialize(): Promise<boolean> {
    if (this.isInitialized) {
      return false
    }

    // Skip version check in local dev environment
    if (import.meta.env.DEV) {
      console.log('[Version] Dev environment - skipping version check')
      this.isInitialized = true
      return false
    }

    // Get stored version from localStorage
    this.previousVersion = localStorage.getItem(VERSION_STORAGE_KEY)

    // Fetch current version from server
    this.currentVersion = await this.fetchVersionInfo()

    if (!this.currentVersion) {
      console.warn('[Version] Could not fetch version info')
      this.isInitialized = true
      return false
    }

    console.log('[Version] Current:', this.currentVersion.version)
    console.log('[Version] Previous:', this.previousVersion || 'none (fresh install)')

    // Check if version has changed
    const hasVersionChanged =
      this.previousVersion !== null && this.previousVersion !== this.currentVersion.version

    if (hasVersionChanged) {
      console.log('[Version] Version changed - clearing caches')
      await this.handleVersionChange()
      this.isInitialized = true
      return true
    }

    // Store version on first load
    if (!this.previousVersion) {
      console.log('[Version] First load - storing version')
      localStorage.setItem(VERSION_STORAGE_KEY, this.currentVersion.version)
    }

    this.isInitialized = true
    return false
  }

  /**
   * Fetch version info from server
   */
  private async fetchVersionInfo(): Promise<VersionInfo | null> {
    try {
      // Cache bust with timestamp
      const response = await fetch(`/version.json?t=${Date.now()}`)
      if (!response.ok) {
        return null
      }
      return await response.json()
    } catch (error) {
      console.error('[Version] Failed to fetch version info:', error)
      return null
    }
  }

  /**
   * Handle version change - clear caches and update stored version
   */
  private async handleVersionChange(): Promise<void> {
    console.log('[Version] Handling version change...')

    try {
      // Clear all caches
      await this.clearAllCaches()

      // Update stored version
      if (this.currentVersion) {
        localStorage.setItem(VERSION_STORAGE_KEY, this.currentVersion.version)
        localStorage.setItem(LAST_CLEAR_KEY, Date.now().toString())
      }

      console.log('[Version] Version change handled successfully')
    } catch (error) {
      console.error('[Version] Failed to handle version change:', error)
    }
  }

  /**
   * Clear all application caches
   */
  public async clearAllCaches(): Promise<void> {
    console.log('[Version] Clearing all caches...')

    const results = {
      indexedDB: false,
      localStorage: false,
      sessionStorage: false,
      serviceWorker: false,
    }

    // 1. Clear IndexedDB
    try {
      await this.clearIndexedDB()
      results.indexedDB = true
      console.log('[Version] IndexedDB cleared')
    } catch (error) {
      console.error('[Version] Failed to clear IndexedDB:', error)
    }

    // 2. Clear localStorage (except auth tokens and critical data)
    try {
      this.clearLocalStorage()
      results.localStorage = true
      console.log('[Version] localStorage cleared (preserved critical data)')
    } catch (error) {
      console.error('[Version] Failed to clear localStorage:', error)
    }

    // 3. Clear sessionStorage
    try {
      sessionStorage.clear()
      results.sessionStorage = true
      console.log('[Version] sessionStorage cleared')
    } catch (error) {
      console.error('[Version] Failed to clear sessionStorage:', error)
    }

    // 4. Clear Service Worker caches
    try {
      await this.clearServiceWorkerCaches()
      results.serviceWorker = true
      console.log('[Version] Service Worker caches cleared')
    } catch (error) {
      console.error('[Version] Failed to clear Service Worker caches:', error)
    }

    console.log('[Version] Cache clearing results:', results)
  }

  /**
   * Clear IndexedDB databases
   */
  private async clearIndexedDB(): Promise<void> {
    // Get all database names if supported
    if ('databases' in indexedDB) {
      try {
        const databases = await indexedDB.databases()
        for (const db of databases) {
          if (db.name) {
            indexedDB.deleteDatabase(db.name)
            console.log(`[Version] Deleted IndexedDB: ${db.name}`)
          }
        }
      } catch (error) {
        console.warn('[Version] Could not enumerate IndexedDB databases:', error)
      }
    }

    // Also try to delete known database names
    const knownDatabases = ['social-cache', 'cc-platform-sdk-cache', 'offline-audio-db']
    for (const dbName of knownDatabases) {
      try {
        indexedDB.deleteDatabase(dbName)
      } catch {
        // Ignore errors for non-existent databases
      }
    }
  }

  /**
   * Clear localStorage but preserve critical data
   */
  private clearLocalStorage(): void {
    const keysToPreserve = [
      // Auth tokens
      'auth_token',
      'access_token',
      'refresh_token',
      'token_expiry',
      'user_profile',
      'selected_profile_id',
      // User preferences
      'theme_preference',
      'theme',
      // Site/tenant settings
      'ui_site_type',
      'ui_site_logo',
      'ui_site_website',
      'ui_tenant_id',
      'tenant_id',
      // Version tracking
      VERSION_STORAGE_KEY,
      LAST_CLEAR_KEY,
      // PWA
      'lastDismissedVersion',
      'lastUpdateDeferred',
      'pwa_install_dismissed',
      PWA_STANDALONE_USED_KEY,
    ]

    // Save critical data
    const preserved: Record<string, string | null> = {}
    keysToPreserve.forEach((key) => {
      preserved[key] = localStorage.getItem(key)
    })

    // Clear all
    localStorage.clear()

    // Restore critical data
    Object.entries(preserved).forEach(([key, value]) => {
      if (value !== null) {
        localStorage.setItem(key, value)
      }
    })
  }

  /**
   * Clear all Service Worker caches
   */
  private async clearServiceWorkerCaches(): Promise<void> {
    if (!('caches' in window)) {
      console.warn('[Version] Cache API not supported')
      return
    }

    try {
      const cacheNames = await caches.keys()
      await Promise.all(cacheNames.map((name) => caches.delete(name)))
      console.log(`[Version] Deleted ${cacheNames.length} cache(s)`)
    } catch (error) {
      console.error('[Version] Failed to clear Service Worker caches:', error)
      throw error
    }
  }

  /**
   * Force update - clear everything and reload
   */
  public async forceUpdate(): Promise<void> {
    console.log('[Version] Forcing update...')

    await this.clearAllCaches()

    // Unregister service workers
    if ('serviceWorker' in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations()
        for (const registration of registrations) {
          await registration.unregister()
        }
        console.log('[Version] Service workers unregistered')
      } catch (error) {
        console.error('[Version] Failed to unregister service workers:', error)
      }
    }

    // Update version
    if (this.currentVersion) {
      localStorage.setItem(VERSION_STORAGE_KEY, this.currentVersion.version)
    }

    // Hard reload
    console.log('[Version] Reloading page...')
    window.location.reload()
  }

  /**
   * Get current version info
   */
  public getCurrentVersion(): VersionInfo | null {
    return this.currentVersion
  }

  /**
   * Get previous version
   */
  public getPreviousVersion(): string | null {
    return this.previousVersion
  }

  /**
   * Check if this is a fresh install (no previous version)
   */
  public isFreshInstall(): boolean {
    return !this.previousVersion
  }

  /**
   * Get time since last cache clear in milliseconds
   */
  public getTimeSinceLastClear(): number | null {
    const lastClear = localStorage.getItem(LAST_CLEAR_KEY)
    if (!lastClear) return null

    const timestamp = parseInt(lastClear, 10)
    return Date.now() - timestamp
  }

  /**
   * Manual cache clear (for debugging/user request)
   */
  public async manualCacheClear(): Promise<void> {
    await this.clearAllCaches()
    localStorage.setItem(LAST_CLEAR_KEY, Date.now().toString())
    console.log('[Version] Manual cache clear completed')
  }

  /**
   * Check if update notification should be shown
   */
  public hasUserFacingChanges(): boolean {
    return this.currentVersion?.hasUserFacingChanges ?? false
  }

  /**
   * Get update message if available
   */
  public getUpdateMessage(): string | null {
    return this.currentVersion?.updateMessage ?? null
  }

  /**
   * Get list of new features
   */
  public getFeatures(): string[] {
    return this.currentVersion?.features ?? []
  }
}

// Export singleton instance
export const versionManager = new VersionManager()
export default versionManager
